import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Lock, ShieldCheck, Sparkles, Loader2, CheckCircle, Clock, Eye, EyeOff } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { userService } from '../_services';
import PasswordStrengthMeter, {
    isPasswordStrong,
    PASSWORD_NOT_STRONG_ENOUGH,
    passwordHasDisallowedWhitespace,
    PASSWORD_SPACE_NOT_ALLOWED,
} from '@/components/ui/PasswordStrengthMeter';
import { buildPasswordPolicyContext, getPasswordPolicyViolationMessage } from '@/_helpers/passwordPolicy';
import * as signalR from '@aspnet/signalr';

const FADE_MS = 150;

// ── Nudge screen (phase 1) ─────────────────────────────────────────────────
function NudgeScreen({ onContinue }) {
    return (
        <div className="space-y-5 text-center pt-2">
            <div className="relative mx-auto flex h-28 w-28 items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-primary/10 blur-xl" aria-hidden />
                <Sparkles className="absolute -top-1 -right-1 h-5 w-5 text-primary/40" aria-hidden />
                <Sparkles className="absolute -bottom-0.5 -left-1 h-4 w-4 text-primary/30" aria-hidden />
                <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-primary/15 ring-4 ring-primary/10">
                    <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
                        <Lock className="h-7 w-7" aria-hidden />
                    </div>
                </div>
            </div>

            <div className="space-y-2">
                <h2 className="text-xl font-semibold text-foreground leading-tight px-1">
                    Update your password<br />for better security
                </h2>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                For your account security, you need to set a new password before continuing. You'll be signed out automatically once it's updated so you can log back in securely.
                </p>
            </div>

            <div className="space-y-3 pt-1">
                <Button className="w-full font-semibold" size="lg" onClick={onContinue}>
                    Update Password Now
                </Button>
                <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground pt-1">
                    <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-primary/70" aria-hidden />
                    For your security, you&apos;ll be asked to set a new password.
                </p>
            </div>
        </div>
    );
}

// ── Success screen (phase 3) ──────────────────────────────────────────────
const SUCCESS_COUNTDOWN = 5;

function SuccessScreen({ onDone }) {
    const [count, setCount] = useState(SUCCESS_COUNTDOWN);

    useEffect(() => {
        if (count <= 0) { onDone(); return; }
        const id = setTimeout(() => setCount((c) => c - 1), 1000);
        return () => clearTimeout(id);
    }, [count, onDone]);

    return (
        <div className="space-y-5 text-center pt-2">
            <div className="relative mx-auto flex h-28 w-28 items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-green-500/10 blur-xl" aria-hidden />
                <Sparkles className="absolute -top-1 -right-1 h-5 w-5 text-green-400/60" aria-hidden />
                <Sparkles className="absolute -bottom-0.5 -left-1 h-4 w-4 text-green-400/40" aria-hidden />
                <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-green-500/15 ring-4 ring-green-500/10">
                    <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white shadow-md">
                        <CheckCircle className="h-7 w-7" aria-hidden />
                    </div>
                </div>
            </div>

            <div className="space-y-2">
                <h2 className="text-xl font-semibold text-foreground leading-tight px-1">
                    Password updated successfully.
                </h2>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                    You&apos;ll be redirected to the login screen shortly.
                </p>
            </div>

            <div className="flex justify-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-sm text-muted-foreground">
                    <Clock className="h-4 w-4 shrink-0" aria-hidden />
                    Redirecting in{' '}
                    <span className="font-semibold text-primary">{count}</span>{' '}
                    seconds...
                </div>
            </div>

            <Button className="w-full font-semibold" size="lg" onClick={onDone}>
                Go to Login
            </Button>
        </div>
    );
}

// ── Form screen (phase 2) ─────────────────────────────────────────────────
function FormScreen({ onPasswordChanged, userdata, policyContext }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPwd, setCurrentPwd] = useState('');
    const [newPwd, setNewPwd] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [errors, setErrors] = useState({});
    const [showCurrentPwd, setShowCurrentPwd] = useState(false);
    const [showNewPwd, setShowNewPwd] = useState(false);
    const [showConfirmPwd, setShowConfirmPwd] = useState(false);

    const resetSessionSignalR = useCallback(() => {
        try {
            const userAgent = window.navigator.userAgent;
            localStorage.setItem('ch_cred_upl_dt', new Date().toString());
            const connection = new signalR.HubConnectionBuilder()
                .withUrl('/resetSession', { skipNegotiation: true, transport: signalR.HttpTransportType.WebSockets })
                .build();
            connection.start().then(() => {
                connection.invoke('UpdateUserLoginPassword', userdata?.id, 'demo', userAgent, userdata?.email)
                    .catch(() => {});
            });
        } catch { /* best-effort */ }
    }, [userdata]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        const newErrors = {};
        setError('');

        if (!currentPwd) {
            newErrors.currentPwd = 'Current password is required';
        } else if (userService.ValidateSpace(currentPwd)) {
            newErrors.currentPwd = 'Space not acceptable';
        }

        if (!newPwd) {
            newErrors.newPwd = 'New password is required';
        } else if (userService.ValidateSpace(newPwd)) {
            newErrors.newPwd = 'Space not acceptable';
        } else if (passwordHasDisallowedWhitespace(newPwd)) {
            newErrors.newPwd = PASSWORD_SPACE_NOT_ALLOWED;
        } else if (newPwd.length < 8) {
            newErrors.newPwd = 'Password must be at least 8 characters';
        } else if (currentPwd && newPwd === currentPwd) {
            newErrors.newPwd = 'New password must be different from your current password';
        } else if (!isPasswordStrong(newPwd, policyContext)) {
            newErrors.newPwd = getPasswordPolicyViolationMessage(newPwd, policyContext) || PASSWORD_NOT_STRONG_ENOUGH;
        }

        if (!confirmPwd) {
            newErrors.confirmPwd = 'Confirm new password is required';
        } else if (newPwd !== confirmPwd) {
            newErrors.confirmPwd = 'New Password and Confirm Password must match';
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        setLoading(true);
        try {
            const response = await fetch('/api/Account/requestpwdupdate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Browser-Url': window.location.href },
                body: JSON.stringify({
                    UserEmail: userdata?.email,
                    CurrentPassword: currentPwd.trim(),
                    NewPassword: newPwd.trim(),
                    RequestURL: window.location.origin,
                }),
            });
            const data = await response.json();
            if (data?.length > 0) {
                if (data[0].status_code === '1') {
                    resetSessionSignalR();
                    onPasswordChanged();
                } else {
                    setError(data[0].status_message);
                }
            } else {
                setError('Whoops, something went wrong. Please try again');
            }
        } catch {
            setError('Whoops, something went wrong. Please try again');
        } finally {
            setLoading(false);
        }
    }, [currentPwd, newPwd, confirmPwd, userdata, resetSessionSignalR, policyContext, onPasswordChanged]);

    return (
        <>
            <DialogHeader>
                <DialogTitle>Reset Password</DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-4">
                <form id="pwd-flow-form" onSubmit={handleSubmit} className="flex flex-col gap-4" autoComplete="off">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="pwf-currentPwd">Current Password</Label>
                        <div className="relative">
                            <Input
                                id="pwf-currentPwd"
                                type={showCurrentPwd ? 'text' : 'password'}
                                value={currentPwd}
                                onChange={(e) => { setCurrentPwd(e.target.value); setErrors(p => ({ ...p, currentPwd: '' })); }}
                                autoComplete="off"
                                maxLength={16}
                                className={`pr-10${errors.currentPwd ? ' border-destructive' : ''}`}
                            />
                            <button type="button" tabIndex={-1} onClick={() => setShowCurrentPwd(v => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none" aria-label={showCurrentPwd ? 'Hide password' : 'Show password'}>
                                {showCurrentPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                        <div className="flex justify-end text-xs text-muted-foreground">
                            Forgot your current password?{' '} &nbsp;
                            <a
                                href="/resetPassword"
                                className="text-primary hover:text-secondary transition-colors duration-300 no-underline"
                            >
                                Reset via email
                            </a>
                        </div>
                        {errors.currentPwd && <p className="text-sm text-destructive">{errors.currentPwd}</p>}
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="pwf-newPwd">New Password</Label>
                        <div className="relative">
                            <Input
                                id="pwf-newPwd"
                                type={showNewPwd ? 'text' : 'password'}
                                value={newPwd}
                                onChange={(e) => { setNewPwd(e.target.value); setErrors(p => ({ ...p, newPwd: '' })); }}
                                autoComplete="off"
                                maxLength={16}
                                className={`pr-10${errors.newPwd ? ' border-destructive' : ''}`}
                            />
                            <button type="button" tabIndex={-1} onClick={() => setShowNewPwd(v => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none" aria-label={showNewPwd ? 'Hide password' : 'Show password'}>
                                {showNewPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                        <PasswordStrengthMeter password={newPwd} policyContext={policyContext} />
                        {errors.newPwd && <p className="text-sm text-destructive">{errors.newPwd}</p>}
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="pwf-confirmPwd">Confirm New Password</Label>
                        <div className="relative">
                            <Input
                                id="pwf-confirmPwd"
                                type={showConfirmPwd ? 'text' : 'password'}
                                value={confirmPwd}
                                onChange={(e) => { setConfirmPwd(e.target.value); setErrors(p => ({ ...p, confirmPwd: '' })); }}
                                autoComplete="off"
                                maxLength={16}
                                className={`pr-10${errors.confirmPwd ? ' border-destructive' : ''}`}
                            />
                            <button type="button" tabIndex={-1} onClick={() => setShowConfirmPwd(v => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground focus:outline-none" aria-label={showConfirmPwd ? 'Hide password' : 'Show password'}>
                                {showConfirmPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                        {errors.confirmPwd && <p className="text-sm text-destructive">{errors.confirmPwd}</p>}
                    </div>

                    {error && (
                        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive">
                            <p className="text-sm">{error}</p>
                        </div>
                    )}
                </form>
            </div>

            <DialogFooter className="px-0 sm:px-0">
                <Button type="submit" form="pwd-flow-form" disabled={loading} className="w-full sm:w-auto">
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Submit
                </Button>
            </DialogFooter>
        </>
    );
}

// ── Combined flow — single Dialog, content cross-fades between phases ──────
/**
 * Mandatory post-login password-change flow (issue #2842).
 *
 * A single Dialog stays open for the full flow — overlay never blinks.
 * Phase 1 (nudge): informs the user and offers a CTA.
 * Phase 2 (form):  the actual password-change form.
 *
 * Props:
 *   open       {boolean}      – controls Dialog visibility
 *   onSuccess  {() => void}   – called after a successful password change
 */
export default function PasswordChangeFlow({ open, onSuccess }) {
    const [phase, setPhase] = useState('nudge');        // 'nudge' | 'form' | 'success'
    const [visible, setVisible] = useState(true);       // drives the opacity cross-fade
    const [dialogOpen, setDialogOpen] = useState(open); // closed before logout so dialog animates out

    const userdata = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
    }, []);

    const policyContext = useMemo(
        () => buildPasswordPolicyContext({
            email: userdata?.email,
            username: userdata?.username ?? userdata?.userName ?? '',
            fullName: userdata?.fullName ?? '',
        }),
        [userdata]
    );

    const transitionTo = useCallback((nextPhase) => {
        setVisible(false);
        setTimeout(() => {
            setPhase(nextPhase);
            setVisible(true);
        }, FADE_MS);
    }, []);

    // Close the dialog first so it animates out, then hand off to the orchestrator.
    const handleDone = useCallback(() => {
        setDialogOpen(false);
        setTimeout(onSuccess, FADE_MS + 30);
    }, [onSuccess]);

    return (
        <Dialog open={dialogOpen}>
            {/* No X button, no Escape — the user must complete the flow */}
            <DialogContent
                className="sm:max-w-md [&>button:last-child]:hidden"
                aria-describedby={undefined}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <DialogTitle className="sr-only">Password update required</DialogTitle>

                <div
                    className="transition-opacity"
                    style={{ opacity: visible ? 1 : 0, transitionDuration: `${FADE_MS}ms` }}
                >
                    {phase === 'nudge' && (
                        <NudgeScreen onContinue={() => transitionTo('form')} />
                    )}
                    {phase === 'form' && (
                        <FormScreen
                            onPasswordChanged={() => transitionTo('success')}
                            userdata={userdata}
                            policyContext={policyContext}
                        />
                    )}
                    {phase === 'success' && (
                        <SuccessScreen onDone={handleDone} />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
